import { useRef, useState, MouseEvent, FC } from 'react'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { alpha, styled } from '@mui/material/styles'
import { Box, Grow, Paper, Popper } from '@mui/material'
import { ContextMenuBaseProps, ContextMenuItem } from './interfaces'
import { ContextMenuList } from './ContextMenuList'

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'alignRight'
})<{ alignRight?: boolean }>(({ alignRight }) => ({
  position: 'relative',
  display: 'flex',

  ...(alignRight && {
    justifyContent: 'flex-end'
  })
}))

const MenuPopper = styled(Popper, {
  shouldForwardProp: (prop) => prop !== 'dense' && prop !== 'fullWidth' && prop !== 'mobileMode'
})<{ dense?: boolean; fullWidth?: boolean; mobileMode?: boolean }>(({ dense, fullWidth, mobileMode, theme }) => ({
  minWidth: theme.spacing(17.5),
  zIndex: 9999,
  ...(dense && {
    minWidth: theme.spacing(13)
  }),
  ...(fullWidth && {
    width: '100%'
  }),
  ...(mobileMode && {
    top: '0 !important',
    transform: 'none !important',
    willChange: 'transform',
    width: '100%',

    '&:before': {
      content: '""',
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100vh',
      width: '100vw',
      zIndex: -1,
      background: alpha(theme.palette.brand.navyBlue, 0.3),
      pointerEvents: 'none'
    }
  })
}))

const MenuPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'mobileMode'
})<{ mobileMode?: boolean }>(({ mobileMode }) => ({
  ...(mobileMode && {
    bottom: 0,
    position: 'fixed',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    transform: 'translate(0, 100vh) !important',
    width: '100vw'
  })
}))

interface ContextMenuBaseComponentProps extends ContextMenuBaseProps {
  ButtonComponent: FC<{ onClick: (event: MouseEvent) => void }>
  ariaLabel?: string
  'data-testid'?: string
}

export function ContextMenuBase({
  ButtonComponent,
  onClickItem,
  isOpen,
  popperClassName,
  fullWidth,
  ariaLabel,
  elevation = 8,
  mobileMode,
  dense,
  placement,
  className,
  alignRight,
  disablePortal = false,
  MenuListComponent = ContextMenuList,
  'data-testid': dataTestId,
  ...menuProps
}: ContextMenuBaseComponentProps) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  const handleToggle = (event) => {
    event.stopPropagation()
    isOpen && isOpen(!open)
    setOpen((prevOpen) => !prevOpen)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClose = (event?: any) => {
    if (event && anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    isOpen && isOpen(false)
    setOpen(false)

    setTimeout(() => {
      if (anchorRef.current) {
        ;(anchorRef.current.firstElementChild as HTMLButtonElement)?.focus()
      }
    })
  }

  const handleClickItem = (item: ContextMenuItem, e?: MouseEvent) => {
    e?.stopPropagation()
    onClickItem(item, e)
    handleClose()
  }

  return (
    <Root
      data-testid={dataTestId}
      ref={anchorRef}
      className={className}
      alignRight={alignRight}
      aria-label={ariaLabel}
      data-open={open}
    >
      <ButtonComponent onClick={handleToggle} />
      <MenuPopper
        disablePortal={disablePortal}
        className={popperClassName}
        dense={dense}
        fullWidth={fullWidth}
        mobileMode={mobileMode}
        popperOptions={{ strategy: mobileMode ? 'fixed' : 'absolute' }}
        anchorEl={anchorRef.current}
        open={open}
        role={undefined}
        {...(placement && { placement })}
        {...((fullWidth || mobileMode) && { disablePortal: true })}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: `${placement.includes('end') ? 'right' : 'center'} ${
                placement.includes('bottom') ? 'top' : 'bottom'
              }`
            }}
          >
            <MenuPaper
              elevation={elevation}
              mobileMode={mobileMode}
              onKeyDown={(ev) => {
                if (ev.key === 'Escape') {
                  handleClose()
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <MenuListComponent onClickItem={handleClickItem} dense={dense} {...menuProps} />
                </Box>
              </ClickAwayListener>
            </MenuPaper>
          </Grow>
        )}
      </MenuPopper>
    </Root>
  )
}
